<script lang="ts">
import Modal from './Modal.vue'
import Loading from './Loading.vue'
import { nextTick, ref, PropType, defineComponent } from 'vue'
import { Illust, User, RoomSummary } from '../../../libs/models'
import { useApi } from '../libs/api'
import { AxiosResponse } from 'axios'
import { ILLUSTS_PER_PAGE } from '../../../libs/constants'
import Stamps from './Stamps.vue'
import { useRoomStore } from '../libs/roomStore'
import illustUrl from '../libs/illustUrl'
import IllustModal from './IllustModal.vue'

const isBottom = (elm: HTMLElement) => {
  const scrollBottom = elm.scrollTop + elm.clientHeight
  return (elm.scrollHeight - scrollBottom) <= 20
}
export default defineComponent({
  components: { IllustModal, Modal, Loading, Stamps },
  emits: {
    close: () => true,
    select: (illust: Illust) => true
  },
  props: {
    user: { type: Object as PropType<User> },
    tag: { type: String },
    emit: { type: Boolean, default: false }
  },
  setup (props, { emit }) {
    const api = useApi()
    const roomStore = useRoomStore()
    const room = roomStore.value
    const isLoading = ref(false)
    const completed = ref(false)
    const page = ref(0)
    const illusts = ref<Illust[]>([])
    const selectedIllust = ref<null | Illust>(null)
    const scrollContainer = ref<HTMLElement>()
    let savedScrollTop = 0
    let needsRefresh = false
    const isTagMode = !!props.tag
    const isUserMode = !!props.user
    const fetchRecords = () => {
      if (isLoading.value || completed.value) return
      const params = { page: page.value + 1 }
      isLoading.value = true
      const apiUrl = props.user ? `/users/${props.user.id}/illusts` : `/tags/${encodeURIComponent(props.tag!)}/illusts`
      api.get(apiUrl, { params }).then(({ data }: AxiosResponse<Illust[]>) => {
        page.value++
        if (!illusts.value) illusts.value = []
        illusts.value.push(...data)
        if (data.length < ILLUSTS_PER_PAGE) completed.value = true
      }).finally(() => {
        isLoading.value = false
      })
    }
    const onScroll = (e: Event) => {
      if (isBottom(e.target as HTMLElement)) fetchRecords()
    }
    fetchRecords()
    const onSelect = (illust: Illust) => {
      if (props.emit) {
        emit('select', illust)
        return
      }
      savedScrollTop = scrollContainer.value?.scrollTop || 0
      selectedIllust.value = illust
    }
    const closeIllust = () => {
      selectedIllust.value = null
      if (needsRefresh) {
        needsRefresh = false
        page.value = 0
        completed.value = false
        illusts.value = []
        savedScrollTop = 0
        fetchRecords()
        return
      }
      nextTick(() => {
        if (scrollContainer.value) scrollContainer.value.scrollTop = savedScrollTop
      })
    }
    const onDeleted = () => {
      const deletedIllust = selectedIllust.value as Illust
      const i = illusts.value.findIndex(v => v === deletedIllust)
      illusts.value.splice(i, 1)
      if (room) {
        const index = room.illusts.findIndex(v => v.id === deletedIllust.id)
        if (index !== -1) {
          room.illusts.splice(index, 1)
        }
      }
      closeIllust()
    }
    return {
      illusts,
      isLoading,
      completed,
      onScroll,
      Loading,
      selectedIllust,
      scrollContainer,
      onSelect,
      closeIllust,
      onDeleted,
      markForRefresh: () => { needsRefresh = true },
      illustUrl,
      isTagMode,
      isUserMode
    }
  }
})
</script>

<template>
  <IllustModal v-if="selectedIllust" :illust="selectedIllust" @deleted="onDeleted" @updated="markForRefresh" @close="closeIllust" />
  <Modal @close="$emit('close')" v-else>
    <div class="ModalContent">
      <div class="ModalTitle">
        <span v-if="isUserMode">{{ l('{name}さんのイラスト一覧', { name: user?.name ?? '' }) }}</span>
        <span v-else-if="isTagMode">{{ l('「#{tag}」タグのイラスト一覧', { tag: tag ?? '' }) }}</span>
      </div>
      <div ref="scrollContainer" class="ScrollContainer" @scroll="onScroll">
        <ul class="IllustsList" v-if="illusts?.length">
          <li class="IllustsList-item" v-for="illust in illusts" :key="illust.id" @click="onSelect(illust)">
            <div class="IllustsList-item-title">
              『{{ illust.title }}』
              <small v-if="isTagMode">by {{ illust.user?.name }}</small>
              <span class="IllustsList-item-title-comment" v-if="illust.comment_count">
                <span class="material-symbols-rounded">comment</span> {{ illust.comment_count }}
              </span>
            </div>
            
            <div class="IllustsList-item-img">
              <div class="inner"><img :src="illustUrl(illust.user_id, illust.id)"></img></div>
              <span class="material-symbols-rounded keep" v-if="illust.pinned">keep</span>
            </div>
            <Stamps :illust="illust" :readonly="true" />
          </li>
        </ul>
        <Loading v-if="isLoading" />
        <div class="EmptyText" v-else-if="!illusts?.length">{{ l('イラストがありません') }}</div>
      </div>
      <button class="cancel" @click="$emit('close')">{{ l('閉じる') }}</button>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.ModalContent {
  line-height: 1.5;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.EmptyText {
  padding: 20px 10px;
  text-align: center;
  font-size: 13px;
}
.ScrollContainer {
  overflow-y: auto;
  margin-bottom: 10px;
}
.IllustsList {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  min-height: 101%; // 無限スクロールのために必ずスクロールを効かせたい
  &-item {
    margin: 5px auto 15px;
    width: calc(33.333% - 6.666px);
    background: var(--bg-color);
    cursor: pointer;
    overflow: hidden;
    text-align: center;
    &-title {
      font-size: 13px;
      font-weight: bold;
      small {
        font-size: 10px;
      }
      &-comment {
        margin-left: 2px;
        display: inline-flex;
        align-items: center;
        vertical-align: middle;
        font-size: 12px;
        .material-symbols-rounded {
          margin-right: 2px;
          font-size: 14px;
        }
      }
    }
    &-img {
      position: relative;
      margin: 4px auto 2px;
      img {
        display: block;
      }
      .inner {
        border-radius: 10px;
        overflow: hidden;
      }
      .material-symbols-rounded.keep {
        position: absolute;
        top: -8px;
        right: -3px;
        font-variation-settings: 'FILL' 1;
        font-size: 26px;
        transform: rotate(45deg);
        color: var(--orange);
        text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.3);
      }
    }
  }
  &::after {
    content: '';
    display: block;
    width: calc(33.333% - 6.666px);
  }
  ::v-deep(.Stamps-list-item) {
    font-size: 12px;
    small {
      font-size: 11px;
    }
  }
}
button {
  margin-top: 16px;
}
</style>
